// src/components/character/CharacterShareModal.tsx
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import { XMarkIcon, ClipboardIcon, CheckIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { QRCodeCanvas } from 'qrcode.react';

interface CharacterShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  characterId: string;
  characterName: string;
}

const CharacterShareModal: React.FC<CharacterShareModalProps> = ({
  isOpen,
  onClose,
  characterId,
  characterName
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isDownloaded, setIsDownloaded] = useState(false);

  if (!isOpen) return null;

  // URL生成
  const hostname = window.location.hostname;
  const isDeployedEnv = hostname === 'orifoli.art' || hostname === 'www.orifoli.art' || hostname.includes('orifoli');
  const characterUrl = `${window.location.origin}/character/${characterId}`;
  const shareUrl = isDeployedEnv
    ? `${window.location.origin}/share/character/${characterId}`
    : characterUrl;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(characterUrl);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  // Web Share API (モバイル用)
  const canUseNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title: `${characterName} | ORIFOLI`,
        text: `${characterName}のプロフィールをチェック！`,
        url: shareUrl,
      });
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        console.error('Share failed:', err);
      }
    }
  };

  const handleShareTwitter = () => {
    const text = `${characterName}のプロフィールをチェック！ #ORIFOLI`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
      window.location.href = url;
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleShareBluesky = () => {
    const text = `${characterName}のプロフィールをチェック！`;
    const blueskyUrl = `https://bsky.app/intent/compose?text=${encodeURIComponent(text)}+${encodeURIComponent(shareUrl)}`;
    if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
      window.location.href = blueskyUrl;
    } else {
      window.open(blueskyUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleShareLine = () => {
    const text = `${characterName}のプロフィールをチェック！`;
    const url = `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`;
    if (/iPhone|iPad|iPod|Android/i.test(navigator.userAgent)) {
      window.location.href = url;
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleDownloadQR = () => {
    const canvas = document.querySelector('#character-qr-code canvas') as HTMLCanvasElement;
    if (canvas) {
      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = `${characterName}-orifoli-qr.png`;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setIsDownloaded(true);
      setTimeout(() => setIsDownloaded(false), 2000);
    }
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              シェア
            </h2>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* QR Code */}
          <div
            id="character-qr-code"
            className="flex justify-center bg-white p-4 rounded-xl mb-4 shadow-inner"
          >
            <QRCodeCanvas
              value={characterUrl}
              size={180}
              level="H"
              includeMargin={true}
              fgColor="#374151"
            />
          </div>

          {/* URL & Copy */}
          <div className="mb-6">
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 text-center truncate px-4">
              {characterUrl}
            </div>
            <button
              onClick={handleCopyLink}
              className="flex items-center justify-center gap-1 mx-auto text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300"
            >
              {isCopied ? (
                <>
                  <CheckIcon className="w-4 h-4" />
                  コピーしました
                </>
              ) : (
                <>
                  <ClipboardIcon className="w-4 h-4" />
                  リンクをコピー
                </>
              )}
            </button>
          </div>

          {/* Native Share (Mobile) */}
          {canUseNativeShare && (
            <button
              onClick={handleNativeShare}
              className="w-full mb-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              共有
            </button>
          )}

          {/* SNS Buttons */}
          <div className="mb-6">
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-3">SNSでシェア</p>
            <div className="flex justify-center gap-3">
              {/* Twitter/X */}
              <button
                onClick={handleShareTwitter}
                className="p-3 bg-black text-white rounded-full hover:bg-gray-800 transition-colors"
                aria-label="Share on X"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </button>

              {/* Bluesky */}
              <button
                onClick={handleShareBluesky}
                className="p-3 bg-[#1185FE] text-white rounded-full hover:bg-[#0d6ecc] transition-colors"
                aria-label="Share on Bluesky"
              >
                <svg className="h-5 w-5" viewBox="0 0 256 256" fill="currentColor">
                  <path d="M 60.901 37.747 C 88.061 58.137 117.273 99.482 127.999 121.666 C 138.727 99.482 167.938 58.137 195.099 37.747 C 214.696 23.034 246.45 11.651 246.45 47.874 C 246.45 55.109 242.302 108.648 239.869 117.34 C 231.413 147.559 200.6 155.266 173.189 150.601 C 221.101 158.756 233.288 185.766 206.966 212.776 C 156.975 264.073 135.115 199.905 129.514 183.464 C 128.487 180.449 128.007 179.038 127.999 180.238 C 127.992 179.038 127.512 180.449 126.486 183.464 C 120.884 199.905 99.024 264.073 49.033 212.776 C 22.711 185.766 34.899 158.756 82.81 150.601 C 55.4 155.266 24.587 147.559 16.13 117.34 C 13.697 108.648 9.55 55.109 9.55 47.874 C 9.55 11.651 41.304 23.034 60.901 37.747 Z"/>
                </svg>
              </button>

              {/* LINE */}
              <button
                onClick={handleShareLine}
                className="p-3 bg-[#06C755] text-white rounded-full hover:bg-[#05a647] transition-colors"
                aria-label="Share on LINE"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C17.5228 2 22 5.59344 22 10.0482C22 14.503 17.5228 18.0964 12 18.0964C11.4116 18.0964 10.8348 18.053 10.2747 17.9698C10.0379 18.3206 8.31001 20.7872 8.14864 20.9718C8.00379 21.1321 7.87162 21.0661 7.87027 21.0248C7.86728 20.927 7.74383 19.9909 7.99787 18.7511L8.00045 18.7345C8.0011 18.7326 8.0018 18.7308 8.00256 18.7289C8.00267 18.7287 8.00277 18.7284 8.00288 18.7281C7.07242 18.1453 6.23093 17.4242 5.51664 16.5969C3.84212 14.6477 2 12.5042 2 10.0482C2 5.59344 6.47715 2 12 2Z"/>
                </svg>
              </button>
            </div>
          </div>

          {/* Download QR */}
          <button
            onClick={handleDownloadQR}
            className="w-full py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            {isDownloaded ? 'ダウンロードしました' : 'QRコードをダウンロード'}
          </button>
        </div>
      </motion.div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default CharacterShareModal;
